import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Avatar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Chip,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Send,
  AttachFile,
  PersonAdd,
  Schedule,
  Phone,
  Email,
  Event,
  Task,
  ExpandMore,
  ExpandLess,
  Edit,
  Add,
  SwapHoriz,
  Create,
} from '@mui/icons-material';

/**
 * Reusable Activity Panel (Chatter) Component
 * Used in forms for messages, logs, and activities
 * 
 * Props:
 * - documentType: 'quotation' | 'salesOrder' | 'invoice' | etc.
 * - documentId: The document's MongoDB _id
 * - documentNumber: Display number (e.g., QT-00001)
 * - onSendMessage: Callback when message is sent
 * - onLogNote: Callback when note is logged
 * - activities: Optional array of activities (for backward compatibility)
 * - fetchActivities: Function to fetch activities from API
 * - user: Current user object
 */

const activityTypeIcons = {
  message: Send,
  note: Task,
  call: Phone,
  email: Email,
  meeting: Event,
  task: Task,
  create: Add,
  update: Edit,
  status_change: SwapHoriz,
};

// Generate avatar color based on name
const getAvatarColor = (name) => {
  const colors = ['#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#009688', '#ff5722', '#795548'];
  if (!name) return colors[0];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const ActivityPanel = ({
  activities: propActivities = [],
  onSendMessage,
  onLogNote,
  onScheduleActivity,
  user,
  showFollowers = false,
  followers = [],
  onAddFollower,
  placeholder = 'Send a message...',
  sx = {},
  // New props for real activity tracking
  documentType,
  documentId,
  documentNumber,
  fetchActivities,
  loading: externalLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [message, setMessage] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [activities, setActivities] = useState(propActivities);
  const [loading, setLoading] = useState(false);

  // Keep latest fetcher in a ref so parents passing inline arrow functions
  // (new reference every render) don't trigger a refetch on every keystroke.
  const fetchActivitiesRef = useRef(fetchActivities);
  useEffect(() => {
    fetchActivitiesRef.current = fetchActivities;
  }, [fetchActivities]);

  const loadActivities = useCallback(async () => {
    const fetcher = fetchActivitiesRef.current;
    if (!fetcher || !documentType || !documentId) return;
    setLoading(true);
    try {
      const result = await fetcher(documentType, documentId);
      if (result?.logs) {
        setActivities(result.logs);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  }, [documentType, documentId]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Update activities when prop changes
  useEffect(() => {
    if (propActivities.length > 0) {
      setActivities(propActivities);
    }
  }, [propActivities]);

  const handleSend = async () => {
    if (!message.trim()) return;

    switch (activeTab) {
      case 0: // Send message
        if (onSendMessage) {
          await onSendMessage({ content: message, attachments });
          // Reload activities after sending
          loadActivities();
        }
        break;
      case 1: // Log note
        if (onLogNote) {
          await onLogNote({ content: message, attachments });
          // Reload activities after logging
          loadActivities();
        }
        break;
      default:
        break;
    }

    setMessage('');
    setAttachments([]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    
    // If today, show time only
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
    }
    
    // If yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    
    // Otherwise show date and time
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ', ' + 
           d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  };

  // Group activities by date
  const groupActivitiesByDate = (activities) => {
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    activities.forEach(activity => {
      const date = new Date(activity.createdAt).toDateString();
      let label;
      if (date === today) {
        label = 'Today';
      } else if (date === yesterday.toDateString()) {
        label = 'Yesterday';
      } else {
        label = new Date(activity.createdAt).toLocaleDateString('en-IN', { 
          day: 'numeric', month: 'short', year: 'numeric' 
        });
      }
      
      if (!groups[label]) groups[label] = [];
      groups[label].push(activity);
    });
    
    return groups;
  };

  // Render change item (old → new)
  const renderChangeItem = (change, index) => (
    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, ml: 2 }}>
      <Typography sx={{ color: '#666', fontSize: '13px', mr: 0.5 }}>•</Typography>
      <Box>
        <Typography component="span" sx={{ color: '#666', fontSize: '13px' }}>
          {change.oldValue}
        </Typography>
        <Typography component="span" sx={{ color: '#999', fontSize: '13px', mx: 0.5 }}>→</Typography>
        <Typography component="span" sx={{ color: '#00A09D', fontSize: '13px', fontWeight: 500 }}>
          {change.newValue}
        </Typography>
        <Typography component="span" sx={{ color: '#999', fontSize: '13px', ml: 0.5 }}>
          ({change.label})
        </Typography>
      </Box>
    </Box>
  );

  // Render activity content based on type
  const renderActivityContent = (activity) => {
    // Odoo-style document type label, e.g. "Customer", "Sales Order"
    const docTypeLabels = {
      quotation: 'Quotation',
      customer: 'Customer',
      product: 'Product',
      invoice: 'Invoice',
      salesOrder: 'Sales Order',
      contact: 'Contact',
    };
    const docLabel = docTypeLabels[documentType]
      || (documentType ? documentType.charAt(0).toUpperCase() + documentType.slice(1) : 'Record');

    switch (activity.type) {
      case 'create':
        return (
          <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
            {docLabel} created
          </Typography>
        );
      case 'update':
        return (
          <Box>
            {activity.changes && activity.changes.length > 0 ? (
              activity.changes.map((change, idx) => renderChangeItem(change, idx))
            ) : (
              <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
                Updated the record
              </Typography>
            )}
          </Box>
        );
      case 'status_change':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ color: '#666', fontSize: '13px' }}>Status changed:</Typography>
            <Chip 
              label={activity.oldStatus} 
              size="small" 
              sx={{ height: 20, fontSize: '11px', bgcolor: '#f5f5f5' }} 
            />
            <Typography sx={{ color: '#999', fontSize: '13px' }}>→</Typography>
            <Chip 
              label={activity.newStatus} 
              size="small" 
              color="primary"
              sx={{ height: 20, fontSize: '11px' }} 
            />
          </Box>
        );
      case 'note':
      case 'message':
        return (
          <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
            {activity.content}
          </Typography>
        );
      default:
        return (
          <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
            {activity.content || activity.message || 'Activity recorded'}
          </Typography>
        );
    }
  };

  const groupedActivities = groupActivitiesByDate(activities);

  return (
    <Box 
      sx={{ 
        bgcolor: 'white', 
        border: '1px solid #e0e0e0', 
        borderRadius: '8px',
        ...sx,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid #e0e0e0',
          bgcolor: '#f8f9fa',
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Activity
        </Typography>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {expanded && (
        <>
          {/* Tabs */}
          <Box sx={{ borderBottom: '1px solid #e0e0e0' }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{
                minHeight: 40,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 40,
                  fontSize: '13px',
                  fontWeight: 500,
                },
              }}
            >
              <Tab label="Send message" />
              <Tab label="Log note" />
              <Tab label="Schedule activity" />
            </Tabs>
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 2 }}>
            {activeTab === 2 ? (
              // Schedule Activity Form
              <Box>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Activity Type"
                  defaultValue="call"
                  sx={{ mb: 2 }}
                  SelectProps={{ native: true }}
                >
                  <option value="call">Phone Call</option>
                  <option value="email">Email</option>
                  <option value="meeting">Meeting</option>
                  <option value="task">Task</option>
                </TextField>
                <TextField
                  size="small"
                  fullWidth
                  label="Summary"
                  placeholder="Enter activity summary"
                  sx={{ mb: 2 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  type="date"
                  label="Due Date"
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  startIcon={<Schedule />}
                  onClick={() => onScheduleActivity && onScheduleActivity()}
                  sx={{
                    bgcolor: '#875A7B',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#714B68' },
                  }}
                >
                  Schedule
                </Button>
              </Box>
            ) : (
              // Message/Note Input
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={activeTab === 0 ? placeholder : 'Log an internal note...'}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" component="label">
                      <AttachFile fontSize="small" />
                      <input type="file" hidden multiple onChange={(e) => setAttachments([...e.target.files])} />
                    </IconButton>
                    {showFollowers && (
                      <IconButton size="small" onClick={onAddFollower}>
                        <PersonAdd fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Send />}
                    onClick={handleSend}
                    disabled={!message.trim()}
                    sx={{
                      bgcolor: '#875A7B',
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#714B68' },
                    }}
                  >
                    {activeTab === 0 ? 'Send' : 'Log'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          {/* Followers */}
          {showFollowers && followers.length > 0 && (
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                Followers ({followers.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {followers.map((follower, index) => (
                  <Chip
                    key={index}
                    avatar={<Avatar sx={{ width: 20, height: 20, fontSize: '10px' }}>{follower.name?.[0]}</Avatar>}
                    label={follower.name}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Activity History */}
          <Box sx={{ borderTop: '1px solid #e0e0e0', maxHeight: 400, overflowY: 'auto', px: 2, py: 2 }}>
            {(loading || externalLoading) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : activities.length === 0 ? (
              <Typography sx={{ color: '#999', fontSize: '13px', textAlign: 'center', py: 2 }}>
                No activity yet
              </Typography>
            ) : (
              Object.entries(groupedActivities).map(([dateLabel, dateActivities]) => (
                <Box key={dateLabel}>
                  {/* Date Divider */}
                  <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                    <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
                    <Typography sx={{ px: 2, color: '#999', fontSize: '13px' }}>{dateLabel}</Typography>
                    <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
                  </Box>
                  
                  {/* Activities for this date */}
                  {dateActivities.map((activity, index) => (
                    <Box key={activity._id || index} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          backgroundColor: getAvatarColor(activity.user?.name),
                          fontSize: '14px'
                        }}
                      >
                        {activity.user?.name?.charAt(0)?.toUpperCase() || 'S'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                          {activity.user?.name || 'System'}{' '}
                          <Typography component="span" sx={{ color: '#999', fontWeight: 400, fontSize: '12px' }}>
                            {formatTime(activity.createdAt)}
                          </Typography>
                        </Typography>
                        {renderActivityContent(activity)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ActivityPanel;
